// react
import React, { useState, useEffect } from "react";

// react-i18next
import { useTranslation } from "react-i18next";

// context
import { UserAuth } from "context/AuthContext";

// react-pdf
import { Document, Page, pdfjs } from "react-pdf";

// @mui
import { Box, IconButton, useMediaQuery, Dialog, Typography, Tooltip } from "@mui/material";
import { KeyboardArrowLeftOutlined as KeyboardArrowLeftOutlinedIcon, KeyboardArrowRightOutlined as KeyboardArrowRightOutlinedIcon, CloseOutlined as CloseOutlinedIcon } from "@mui/icons-material";

// ui-components
import AggregatedTable from "./AggregatedTable";
import DataDialog from "ui-components/certification/DataDialog";
import DatapointIntegrityInspector from "ui-components/certification/DatapointIntegrityInspector";
import { LoadingDialog } from "ui-components/LoadingComponent";

// firebase
import { getFunctions, httpsCallable } from "firebase/functions";

// storage
import downloadFile from "storage/downloadFile";

// verification
import { verifySignature } from "verification";

// A ---------------------------------------------------------------------- M

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const AggregatedViewer = ({ tag, isTagGroupMember }) => {
  const { user } = UserAuth();
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const functions = getFunctions();

  const [record, setRecord] = useState();
  const [verification, setVerification] = useState();
  const [openVerify, setOpenVerify] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const handlePopState = () => {
      if (openVerify) setOpenVerify(false);
      else if (openView) setOpenView(false);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [openVerify, openView]);

  const handleOpenView = () => {
    setOpenView(true);
    window.history.pushState(null, "");
  };

  const handleOpenVerify = () => {
    setOpenVerify(true);
    window.history.pushState(null, "");
  };

  const checkVerification = async (record) => {
    setIsLoading(true);
    try {
      const verification = user ? await verifySignature(user.uid, record, record.type) : await verifySignature(false, record, record.type);
      setVerification(verification);
      handleOpenVerify();
    } catch (err) {
      console.error("Error in checkVerification:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleView = (record) => {
    setRecord(record);
    handleOpenView();
  };

  const handleCloseDocDialog = () => {
    setSelectedFile(null);
    setCurrentPage(1);
  };

  const handleDocumentLoadSuccess = ({ numPages }) => setNumPages(numPages);

  const handlePageChange = (newPage) => setCurrentPage(newPage);

  const handleCloseImageDialog = () => setSelectedImage(null);

  const handlePreview = async (storagePath) => {
    setIsLoading(true);
    try {
      const getUrl = httpsCallable(functions, "getSignedDownloadUrl");
      const { data } = await getUrl({ storagePath, tagId: tag.id });
      const signedUrl = data.url;

      const resp = await fetch(signedUrl);
      const blob = await resp.blob();
      const mime = blob.type.toLowerCase();

      if (mime === "application/pdf") {
        setSelectedFile(signedUrl);
      } else if (mime === "image/png" || mime === "image/jpg" || mime === "image/jpeg") {
        setSelectedImage(signedUrl);
      } else {
        console.warn("Tipo di file non supportato per anteprima:", mime);
      }
    } catch (err) {
      console.error("Error in handlePreview:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileClick = async (fileUrl) => {
    setIsLoading(true);
    try {
      const resp = await fetch(fileUrl);
      const blob = await resp.blob();
      const mime = blob.type.toLowerCase();

      if (mime === "application/pdf") {
        setSelectedFile(fileUrl);
      } else if (mime === "image/png" || mime === "image/jpg" || mime === "image/jpeg") {
        setSelectedImage(fileUrl);
      } else {
        console.warn("Tipo di file non supportato per anteprima:", mime);
      }
    } catch (err) {
      console.error("Error fetching and parsing file:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenFile = async (record) => {
    const data = record.data || {};
    if (data.storagePath) {
      await handlePreview(data.storagePath);
    } else if (data.attachment) {
      await handleFileClick(data.attachment);
    } else {
      console.warn("Nessun campo storagePath o attachment trovato nel record", record);
    }
  };

  const handleDownload = async (record) => {
    setIsLoading(true);
    try {
      const { storagePath, attachment, fileName } = record.data;

      if (storagePath) {
        const getUrl = httpsCallable(functions, "getSignedDownloadUrl");
        const { data } = await getUrl({ storagePath, tagId: tag.id });
        await downloadFile(data.url, fileName || "downloaded_file");
      } else if (attachment) {
        await downloadFile(attachment, fileName || "downloaded_file");
      } else {
        console.warn("Nessun campo storagePath o attachment trovato per il download", record);
      }
    } catch (err) {
      console.error("Error in handleDownload:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading && <LoadingDialog open={isLoading} />}

      {tag && <AggregatedTable tag={tag} isTagGroupMember={isTagGroupMember} checkVerification={checkVerification} handleView={handleView} />}

      {record && <DataDialog data={{ ...record, databoxName: tag.name || "N/A" }} open={openView} onClose={() => setOpenView(false)} handleOpenFile={handleOpenFile} handleDownload={handleDownload} />}

      {verification && <DatapointIntegrityInspector verification={verification} open={openVerify} setOpen={setOpenVerify} />}

      {selectedFile && (
        <Dialog
          open={Boolean(selectedFile)}
          onClose={handleCloseDocDialog}
          fullWidth
          maxWidth="sm"
          sx={{
            "& .MuiPaper-root": {
              borderRadius: "15px",
              padding: 1,
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.5)",
            },
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Tooltip placement="top" title={t("close")}>
              <IconButton onClick={handleCloseDocDialog} color="error" aria-label="close">
                <CloseOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Document file={selectedFile} onLoadSuccess={handleDocumentLoadSuccess}>
            <Page pageNumber={currentPage} width={isMobile ? 300 : 600} renderAnnotationLayer={false} renderTextLayer={false} />
          </Document>
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <IconButton onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} aria-label="previous page">
              <KeyboardArrowLeftOutlinedIcon />
            </IconButton>
            <Typography>{`${currentPage} / ${numPages}`}</Typography>
            <IconButton onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === numPages} aria-label="next page">
              <KeyboardArrowRightOutlinedIcon />
            </IconButton>
          </Box>
        </Dialog>
      )}

      {selectedImage && (
        <Dialog open={Boolean(selectedImage)} onClose={handleCloseImageDialog} fullWidth maxWidth="md">
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Tooltip placement="top" title={t("close")}>
              <IconButton
                color="error"
                sx={{
                  position: "absolute",
                  top: "0%",
                  right: "0%",
                  zIndex: 1,
                }}
                onClick={handleCloseImageDialog}
              >
                <CloseOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <img src={selectedImage} alt="img" />
        </Dialog>
      )}
    </>
  );
};

export default AggregatedViewer;
